import { useEffect, useState } from "react";
import { Loader2, Lock, Unlock, CheckCircle } from "lucide-react";
import { GET_BASE_URL } from "../utils/Constants";
import androidIcon from "../assets/res/android_white.svg";
import appleIcon from "../assets/res/apple_white.svg";

const LOCKER_URL = "https://nolocker.com/cl/v/34lvwm";

const InstallFlow = ({ nicheName = "", imageUrl = "", platform = "", size = "", onUnlock, onClose }) => {
    const [iframeLoading, setIframeLoading] = useState(true);
    const [unlocked, setUnlocked] = useState(false);
    const [countdown, setCountdown] = useState(600);
    const [showExitWarning, setShowExitWarning] = useState(false);

    const iframeUrl = `${LOCKER_URL}?sub_id=${encodeURIComponent(nicheName)}`;

    // Countdown timer
    useEffect(() => {
        const interval = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) { clearInterval(interval); return 0; }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Intercept browser back button
    useEffect(() => {
        window.history.pushState({ offerWall: true }, "");

        const handlePopState = () => {
            setShowExitWarning(true);
            window.history.pushState({ offerWall: true }, "");
        };

        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, []);

    // OGAds completion postMessage
    useEffect(() => {
        const handleMessage = (event) => {
            const data = event.data;
            if (
                data === "ogads_complete" ||
                data === "complete" ||
                data?.type === "complete" ||
                data?.complete === true ||
                data?.status === "complete" ||
                data?.unlocked === true
            ) {
                setUnlocked(true);
            }
        };
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, []);

    const formatTime = (secs) => {
        const m = Math.floor(secs / 60).toString().padStart(2, "0");
        const s = (secs % 60).toString().padStart(2, "0");
        return `${m}:${s}`;
    };

    const handleCloseAttempt = () => {
        if (unlocked) {
            onClose?.();
        } else {
            setShowExitWarning(true);
        }
    };

    return (
        <div className="relative h-full flex flex-col" style={{ background: "linear-gradient(135deg, #012a4a 0%, #013a63 100%)" }}>

            {/* ── STICKY HEADER ── */}
            <div className="flex-shrink-0 border-b border-white/10" style={{ background: "rgba(1, 42, 74, 0.95)", backdropFilter: "blur(12px)" }}>

                {/* Step indicator */}
                <div className="flex items-center justify-between px-4 pt-3 pb-1">
                    <div className="flex items-center gap-1.5">
                        {[1, 2, 3].map((step) => (
                            <div key={step} className="flex items-center gap-1.5">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] semiBold ${
                                    step < 3 ? "bg-green-500 text-white" : "bg-light-blue text-white"
                                }`}>
                                    {step < 3 ? <CheckCircle className="w-4 h-4" /> : "3"}
                                </div>
                                {step < 3 && <div className="w-6 h-[2px] bg-green-500 rounded" />}
                            </div>
                        ))}
                        <span className="text-white/60 text-xs medium ml-1">Step 3 of 3</span>
                    </div>

                    {/* Countdown */}
                    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs semiBold ${
                        countdown <= 60 ? "bg-red-500/20 text-red-400 animate-pulse" : "bg-white/10 text-white/70"
                    }`}>
                        ⏳ {formatTime(countdown)}
                    </div>
                </div>

                {/* Game preview */}
                <div className="flex items-center gap-3 px-4 pb-3">
                    <div className="relative flex-shrink-0">
                        <img
                            src={GET_BASE_URL() + "images/" + imageUrl}
                            alt={nicheName}
                            className={`w-12 h-12 rounded-xl object-cover transition-all duration-500 ${unlocked ? "" : "brightness-50"}`}
                        />
                        {!unlocked && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <Lock className="w-5 h-5 text-white drop-shadow" />
                            </div>
                        )}
                        {unlocked && (
                            <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-0.5">
                                <Unlock className="w-3 h-3 text-white" />
                            </div>
                        )}
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="semiBold text-white text-[15px] truncate">{nicheName}</div>
                        <div className="flex items-center gap-3 mt-0.5">
                            {size && <span className="text-white/50 text-xs medium">📦 {size}</span>}
                            {platform && (
                                <span className="flex items-center gap-1 text-white/50 text-xs medium">
                                    <img src={platform === "ios" ? appleIcon : androidIcon} alt="" className="w-3 h-3" />
                                    {platform === "ios" ? "iOS" : "Android"}
                                </span>
                            )}
                        </div>
                        <div className={`text-xs semiBold mt-0.5 transition-colors duration-500 ${unlocked ? "text-green-400" : "text-orange-400"}`}>
                            {unlocked ? "✓ Unlocked — tap Download below" : "🔒 Complete one offer to unlock"}
                        </div>
                    </div>

                    {/* Close button */}
                    <button
                        onClick={handleCloseAttempt}
                        className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    >
                        <span className="text-white/60 text-sm">✕</span>
                    </button>
                </div>

                {/* Ready nudge */}
                <div className="px-4 pb-3">
                    <div
                        className="flex items-center gap-2 rounded-xl px-3 py-2"
                        style={{ background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.25)" }}
                    >
                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <div className="min-w-0">
                            <div className="semiBold text-green-400 text-[13px]">
                                Your {platform === "ios" ? "IPA" : "APK"} is ready!
                            </div>
                            <div className="medium text-white/60 text-[11px]">
                                Complete one quick step below to start your download.
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── IFRAME ── */}
            <div className="flex-1 relative overflow-hidden">
                <iframe
                    src={iframeUrl}
                    onLoad={() => setIframeLoading(false)}
                    className="w-full h-full border-0"
                    title="Unlock your download"
                    allow="clipboard-write"
                />
                {iframeLoading && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none" style={{ background: "rgba(1, 42, 74, 0.9)" }}>
                        <Loader2 className="w-8 h-8 text-light-blue animate-spin" />
                        <p className="text-white/60 text-sm medium">Loading offers...</p>
                    </div>
                )}
            </div>

            {/* ── STICKY FOOTER ── */}
            <div className="flex-shrink-0 px-4 py-3 border-t border-white/10" style={{ background: "rgba(1, 42, 74, 0.95)", backdropFilter: "blur(12px)" }}>
                <button
                    disabled={!unlocked}
                    onClick={() => unlocked && onUnlock?.()}
                    className={`w-full py-3.5 rounded-2xl semiBold text-base transition-all duration-300 ${
                        unlocked
                            ? "bg-green-500 hover:bg-green-400 text-white shadow-lg shadow-green-900/40 animate-pulse"
                            : "bg-white/10 text-white/30 cursor-not-allowed"
                    }`}
                >
                    {unlocked ? "⬇ Download Now — It's Free" : "Complete an offer above to unlock"}
                </button>
            </div>

            {/* ── EXIT WARNING MODAL ── */}
            {showExitWarning && (
                <div className="absolute inset-0 z-10 flex items-center justify-center px-6" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}>
                    <div className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl">
                        <div className="text-center mb-4">
                            <div className="text-4xl mb-2">⚠️</div>
                            <h2 className="semiBold text-gray-900 text-xl">Leave download?</h2>
                            <p className="medium text-gray-500 text-sm mt-2">
                                Your file is ready and waiting. If you leave now you'll need to start the process over from scratch.
                            </p>
                        </div>

                        {/* Game reminder */}
                        <div className="flex items-center gap-3 bg-gray-50 rounded-2xl p-3 mb-5">
                            <img
                                src={GET_BASE_URL() + "images/" + imageUrl}
                                alt={nicheName}
                                className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
                            />
                            <div className="min-w-0">
                                <div className="semiBold text-sm text-gray-800 truncate">{nicheName}</div>
                                <div className="medium text-xs text-orange-400">🔒 Not yet unlocked</div>
                            </div>
                        </div>

                        <button
                            onClick={() => setShowExitWarning(false)}
                            className="w-full py-3 rounded-2xl bg-semi-blue text-white semiBold text-base mb-2"
                        >
                            Stay and Download
                        </button>
                        <button
                            onClick={() => { setShowExitWarning(false); onClose?.(); }}
                            className="w-full py-2.5 rounded-2xl text-gray-400 medium text-sm"
                        >
                            Leave anyway
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InstallFlow;
